import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

import { API_ERROR_MESSAGES, SESSION_STORAGE_KEYS } from 'src/utils/constants';

import { logError } from './dynatrace';
import { getAuthToken, msalInstance } from './msal';
import { BACKEND_API, APPLICATION_ID } from '../global-config';

const axiosInstance = axios.create({
  baseURL: `${BACKEND_API.baseUrl}${BACKEND_API.prefix}`,
});

axiosInstance.interceptors.request.use(async (config) => {
  try {
    // Do not add headers for s3 buckets calls
    if (config.url?.includes('s3.amazonaws.com')) {
      return config;
    }
    const sessionId = sessionStorage.getItem(SESSION_STORAGE_KEYS.SESSION_ID);
    const idToken = await getAuthToken();
    config.headers.Authorization = `Bearer ${idToken}`;
    config.headers.correlationId = uuidv4();
    config.headers.application = APPLICATION_ID;

    if (sessionId) {
      config.headers[SESSION_STORAGE_KEYS.SESSION_ID] = sessionId;
    }

    return config;
  } catch (error) {
    console.error(error);
  }

  return config;
});

axiosInstance.interceptors.response.use(
  (res) => res,
  (error) => {
    if (axios.isCancel(error)) {
      throw new Error(API_ERROR_MESSAGES.REQUEST_CANCELED);
    }
    logError(error);
    const errorResponse = error.response;

    // Check if the error has a response
    if (errorResponse) {
      // Handle 401 Unauthorized errors when idToken is expired earlier than expected
      if (errorResponse.status === 401) {
        msalInstance.loginRedirect();
        return;
      }

      // Handle 403 Forbidden errors when user does not have access to the resource
      if (errorResponse.status === 403) {
        window.location.href = '/403';
        return;
      }

      // Handle errors from Gateway and backend service
      if (errorResponse.status === 503) {
        throw new Error(API_ERROR_MESSAGES.SERVICE_UNAVAILABLE);
      }

      // For all other errors, return the original errorResponse
      // This allows React Query to access the status code and response data
      throw error;
    } else if (error.code === 'ERR_NETWORK') {
      const isS3Upload = Boolean(error?.config?.url?.includes('s3.amazonaws.com'));
      if (isS3Upload) {
        console.error('Network error occurred while uploading to S3 bucket:', error.message);
        return;
      }

      // This error occurs when the token is expired also
      console.error('Network error occurred:', error.message);
      // msalInstance.loginRedirect();
      return;
    }

    // If no response (network error, etc.), throw a generic error
    throw new Error(API_ERROR_MESSAGES.UNKNOWN_ERROR);
  }
);

export default axiosInstance;
